import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { db } from '@/lib/db/index';
import type { SyncOperation } from '@/lib/db/index';
import {
  getPending, markSyncing, markSynced, markFailed,
  resetToPending, incrementRetry,
} from '@/lib/sync/queue';
import { cacheWorkOrder } from '@/lib/db/cache';
import { markPhotoUploaded, markPhotoFailed } from '@/lib/db/photoStaging';
import {
  startWorkInDB, blockWorkOrderInDB, resumeWorkOrderInDB,
  closeWorkOrderInDB, createWorkOrderInDB, fetchWorkOrder,
} from '@/lib/api/workOrders';
import { apiPost } from '@/lib/api/client';
import type { WorkOrder, ClosureData } from '@/types/maintenance';
import type { BlockWOInput, CreateWOInput } from '@/lib/api/workOrders';

const MAX_RETRY = 3;
// Minimum interval between drain attempts — prevents a retry storm on
// persistent network failures (we were previously retrying instantly on
// every `online` event + every enqueue).
const MIN_DRAIN_INTERVAL_MS = 2_000;
// Exponential backoff between failing ops within a single drain.
// retry_count 0 → 0ms, 1 → 500ms, 2 → 2000ms, 3 → 4500ms
function backoffMs(retryCount: number): number {
  return Math.min(8_000, 500 * retryCount * retryCount);
}
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

let lastDrainAt = 0;

export interface SyncConflict {
  wo_id: string;
  entity_id: string;
  server_updated_at: string;
  local_updated_at: string;
}

export interface DrainResult {
  synced: number;
  failed: number;
  conflicts: SyncConflict[];
}

// ─── Lock — prevents concurrent drains ───────────────────────────────────────
let draining = false;

// ─── Conflict detection ───────────────────────────────────────────────────────

async function checkConflict(op: SyncOperation): Promise<SyncConflict | null> {
  if (!op.entity_id) return null;
  try {
    const data = await fetchWorkOrder(op.entity_id);

    if (!data) return null;
    if (data.updated_at > op.optimistic_updated_at) {
      return {
        wo_id: data.wo_id,
        entity_id: op.entity_id,
        server_updated_at: data.updated_at,
        local_updated_at: op.optimistic_updated_at,
      };
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Photo upload handler ─────────────────────────────────────────────────────

async function handlePhotoUpload(op: SyncOperation, orgId: string): Promise<void> {
  const { staged_photo_id, wo_id, filename } = op.payload as {
    staged_photo_id: string;
    wo_id: string;
    filename: string;
  };

  const staged = await db.photo_stage.get(staged_photo_id);
  if (!staged || staged.upload_status === 'uploaded') return;

  await db.photo_stage.update(staged_photo_id, { upload_status: 'uploading' });

  const path = `${orgId}/${wo_id}/${staged_photo_id}_${filename}`;

  const { error: uploadError } = await supabase.storage
    .from('work-order-attachments')
    .upload(path, staged.blob, { contentType: staged.mime_type, upsert: true });

  if (uploadError) throw new Error(uploadError.message);

  const { data: { publicUrl } } = supabase.storage
    .from('work-order-attachments')
    .getPublicUrl(path);

  // Append URL to work_orders.attachments via API
  await apiPost(`/api/work-orders/${wo_id}/attachments`, {
    url: publicUrl,
    filename,
    created_at: op.created_at,
  });

  await markPhotoUploaded(staged_photo_id, publicUrl);
}

// ─── Main drain ───────────────────────────────────────────────────────────────

export async function drainQueue(
  orgId: string,
  userId: string,
  queryClient: QueryClient,
  onProgress?: (status: 'syncing' | 'done') => void,
): Promise<DrainResult> {
  if (draining) return { synced: 0, failed: 0, conflicts: [] };
  // Throttle: if the previous drain finished less than MIN_DRAIN_INTERVAL_MS ago,
  // skip this one. Lets the caller keep invoking drain() liberally without causing
  // a retry storm against a flaky network.
  const sinceLast = Date.now() - lastDrainAt;
  if (sinceLast < MIN_DRAIN_INTERVAL_MS) {
    return { synced: 0, failed: 0, conflicts: [] };
  }
  draining = true;
  onProgress?.('syncing');

  const result: DrainResult = { synced: 0, failed: 0, conflicts: [] };

  try {
    const ops = await getPending(orgId);

    for (const op of ops) {
      // Ops that have failed too many times stay failed
      if (op.retry_count >= MAX_RETRY) {
        await markFailed(op.id, op.error ?? 'Max retries exceeded');
        result.failed++;
        continue;
      }

      // Exponential backoff between retries within a drain
      if (op.retry_count > 0) {
        await sleep(backoffMs(op.retry_count));
      }

      await markSyncing(op.id);

      // Conflict detection (skip for create/photo — no prior server state)
      if (op.type !== 'wo:create' && op.type !== 'wo:attach_photo') {
        const conflict = await checkConflict(op);
        if (conflict) result.conflicts.push(conflict);
        // Still apply — last-write-wins
      }

      try {
        await dispatchOp(op, userId, orgId);
        await markSynced(op.id);
        result.synced++;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isNetworkError =
          msg.includes('Failed to fetch') ||
          msg.includes('NetworkError') ||
          msg.includes('Load failed') ||
          !navigator.onLine;

        if (isNetworkError) {
          // Connection lost mid-drain — pause and retry later
          await resetToPending(op.id);
          break;
        }

        // Application-level error — mark failed, continue to next op
        await incrementRetry(op.id);
        await markFailed(op.id, msg);
        result.failed++;
      }
    }

    // Refresh React Query so the UI shows authoritative server state
    if (result.synced > 0) {
      queryClient.invalidateQueries({ queryKey: ['work_orders'] });
    }
  } finally {
    draining = false;
    lastDrainAt = Date.now();
    onProgress?.('done');
  }

  return result;
}

// ─── Operation dispatcher ─────────────────────────────────────────────────────

async function dispatchOp(op: SyncOperation, userId: string, orgId: string): Promise<void> {
  switch (op.type) {
    case 'wo:start': {
      const { wo } = op.payload as { wo: WorkOrder };
      await startWorkInDB(wo, userId);
      const fresh = await fetchWorkOrder(wo.id);
      if (fresh) await cacheWorkOrder(fresh);
      break;
    }

    case 'wo:block': {
      const { wo, input } = op.payload as { wo: WorkOrder; input: BlockWOInput };
      await blockWorkOrderInDB(wo, input, userId);
      const fresh = await fetchWorkOrder(wo.id);
      if (fresh) await cacheWorkOrder(fresh);
      break;
    }

    case 'wo:resume': {
      const { wo } = op.payload as { wo: WorkOrder };
      await resumeWorkOrderInDB(wo, userId);
      const fresh = await fetchWorkOrder(wo.id);
      if (fresh) await cacheWorkOrder(fresh);
      break;
    }

    case 'wo:close': {
      const { wo, closureData } = op.payload as { wo: WorkOrder; closureData: ClosureData };
      await closeWorkOrderInDB(wo, closureData, userId);
      const fresh = await fetchWorkOrder(wo.id);
      if (fresh) await cacheWorkOrder(fresh);
      break;
    }

    case 'wo:create': {
      const { input, temp_id } = op.payload as { input: CreateWOInput; temp_id: string };
      const created = await createWorkOrderInDB(input, userId, orgId);
      // Replace the temp IndexedDB record with the canonical server record
      if (temp_id !== created.id) {
        await db.work_orders.delete(temp_id);
      }
      await cacheWorkOrder(created);
      break;
    }

    case 'wo:attach_photo': {
      await handlePhotoUpload(op, orgId);
      break;
    }

    default:
      throw new Error(`Unknown sync op type: ${(op as SyncOperation).type}`);
  }
}
